import { useEffect, useMemo, useState } from 'react';
import { DrillSuggest, ThemeHelpSheet } from '../components/Drills';
import { fmtMoney, fmtNum, getLang, localeOf, translate, useT } from '../lib/i18n';
import { createPortal } from 'react-dom';
import { fmtLongDay, fmtTime } from '../lib/time';
import { alert, haptic } from '../lib/telegram';
import { Vis, VisLabel } from '../components/Vis';
import { GrowArea } from '../components/GrowArea';
import { PLAYER_SEES_SHARED_NOTES } from '../lib/flags';
import { byMonth, CheckIcon, SessionHead, SessionRow as SessionLine } from '../components/SessionRow';
import { LessonPayBlock, LessonPaySheet } from '../components/LessonPayment';

/**
 * The coach-first player profile: level, sessions, one session in full,
 * and the AI read of the notes. ClientDetail lays these out in tabs.
 *
 * Coach only for now. Notes meant for the player (shared_note, homework)
 * are stored apart from the private note so a player view can show them
 * later without ever exposing the private one.
 */

const BASE = import.meta.env.VITE_API_URL ?? '';
let token = '';
export const setProfileToken = (t: string) => { token = t; };

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: { 'content-type': 'application/json', authorization: `Bearer ${token}`, ...(init.headers ?? {}) },
  });
  if (!res.ok) {
    const b = await res.json().catch(() => ({}));
    throw new Error(b.error ?? `http_${res.status}`);
  }
  return res.status === 204 ? (undefined as T) : res.json();
}

export interface Profile {
  /** The coach's sports and the client's (coach/sports.ts); client is null unless the coach teaches tennis too. */
  sport?: { coach: string[]; client: 'padel' | 'tennis' | 'both' | null };
  about: { playSide: 'right' | 'left' | 'both' | null; hand: 'right' | 'left' | null; playtomicLevel: number | null };
  level: { current: number; setAt: string; history: { level: number; at: string; note: string | null }[] } | null;
  workingOn: { text: string; since: string | null; sessionsSince: number } | null;
  stats: { sessions: number; turnedUp: number | null; weeksRunning: number };
  matchesLogged: number;
}

export interface SessionRow {
  booking_id: string; status: string; focus: string[]; shared_note: string | null;
  coach_note: string | null; client_note: string | null; drills: string | null; homework: string | null;
  starts_at: string; ends_at: string; kind: string; title: string; people: number; rated: number;
  /** For a pair or group, which opens the one notes sheet for the whole session. */
  session_id?: string;
  /** Cash still to pay on it, as «Деньги» counts it; 0 when paid or not cash. */
  owed?: number;
}

const SIDE_KEY = { right: 'co.pp.sideRight', left: 'co.pp.sideLeft', both: 'co.pp.sideBoth' } as const;
const HAND_KEY = { right: 'co.pp.handRight', left: 'co.pp.handLeft' } as const;

/** Padel's usual bands on the 0-7 scale, so a number also reads as words. */
export function levelWord(l: number) {
  const key = l < 1.5 ? 'co.pp.lvl.beginner'
    : l < 2.5 ? 'co.pp.lvl.improver'
    : l < 3.5 ? 'co.pp.lvl.intermediate'
    : l < 4.5 ? 'co.pp.lvl.intermediatePlus'
    : l < 5.5 ? 'co.pp.lvl.advanced'
    : l < 6.5 ? 'co.pp.lvl.competition'
    : 'co.pp.lvl.pro';
  return translate(getLang(), key);
}

/** "September", or Russian's "сентября" — the form that follows "с". */
const monthName = (iso: string) => getLang() === 'ru'
  ? new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'long' }).format(new Date(iso)).replace(/^\d+\s*/, '')
  : new Intl.DateTimeFormat('en-GB', { month: 'long' }).format(new Date(iso));
/** A plain day (a note's date, already in the coach's timezone) is shown as that day wherever the phone is. */
const shortDate = (iso: string) =>
  new Intl.DateTimeFormat(localeOf(getLang()), {
    day: 'numeric', month: 'short', ...(/^\d{4}-\d{2}-\d{2}$/.test(iso) ? { timeZone: 'UTC' } : {}),
  }).format(new Date(iso));

/**
 * The server labels each source of the AI read in English, because the
 * same labels go into the prompt. Shown to the coach, they are translated.
 */
const SOURCE_KINDS: Record<string, Parameters<typeof translate>[1]> = {
  'Standing notes': 'co.pp.src.standing',
  'Current Working on': 'co.pp.src.workingOn',
  'Earlier Working on': 'co.pp.src.earlierGoal',
  'Focus': 'co.pp.src.focus',
  'Drills': 'co.pp.src.drills',
  'Private note': 'co.pp.src.private',
  'Note for the player': 'co.pp.src.shared',
  'Note for everyone on the session': 'co.pp.src.common',
  'Homework': 'co.pp.src.homework',
  'Player asked to work on': 'co.pp.src.asked',
};
const sourceKind = (kind: string) =>
  SOURCE_KINDS[kind] ? translate(getLang(), SOURCE_KINDS[kind]) : kind;

// ---------------------------------------------------------------------
//  About chips and the sheet that edits them
// ---------------------------------------------------------------------

export function AboutChips({ about, onEdit }: { about: Profile['about']; onEdit: () => void }) {
  const t = useT();
  const chips = [
    about.playSide && translate(getLang(), SIDE_KEY[about.playSide]),
    about.hand && translate(getLang(), HAND_KEY[about.hand]),
  ].filter(Boolean) as string[];
  return (
    <div className="pp-chips">
      {chips.map((c) => <span key={c} className="pchip">{c}</span>)}
      <button className="pchip pchip--add" onClick={() => { haptic.tap(); onEdit(); }}>
        {chips.length ? t('co.pp.edit') : t('co.pp.addSideHand')}
      </button>
    </div>
  );
}

export function AboutSheet({ clientId, name, about, onClose, onSaved }: {
  clientId: string; name: string; about: Profile['about']; onClose: () => void; onSaved: () => void;
}) {
  const t = useT();
  const [side, setSide] = useState(about.playSide);
  const [hand, setHand] = useState(about.hand);
  const [pt, setPt] = useState(about.playtomicLevel == null ? '' : String(about.playtomicLevel));
  const [busy, setBusy] = useState(false);

  async function save() {
    setBusy(true);
    try {
      await api(`/api/coach/clients/${clientId}/about`, {
        method: 'PUT', body: JSON.stringify({ playSide: side, hand, playtomicLevel: pt === '' ? null : Number(pt) }),
      });
      haptic.success(); onSaved(); onClose();
    } catch (err: any) {
      haptic.error();
      await alert(err.message === 'playtomic_level_must_be_0_to_7' ? t('co.pp.playtomicRange') : t('co.pp.couldNotSave', { message: err.message }));
    } finally { setBusy(false); }
  }

  const opt = <T extends string>(value: T, current: T | null, set: (v: T | null) => void, label: string, sub: string) => (
    <button type="button" className="payer__opt" aria-pressed={current === value}
      onClick={() => { haptic.tap(); set(current === value ? null : value); }}>
      <b>{label}</b><small>{sub}</small>
    </button>
  );

  return createPortal(
    <div className="sheet" onClick={onClose}>
      <div className="sheet__panel sheet--rhythm" onClick={(e) => e.stopPropagation()}>
        <div className="sheet__title">{t('co.pp.howPlays', { name })}</div>
        <div>
          <span className="mf__label">{t('co.pp.side')}</span>
          <div className="payer">
            {opt('right', side, setSide, t('co.pp.right'), t('co.pp.reves'))}
            {opt('left', side, setSide, t('co.pp.left'), t('co.pp.drive'))}
            {opt('both', side, setSide, t('co.pp.both'), t('co.pp.either'))}
          </div>
        </div>
        <div>
          <span className="mf__label">{t('co.pp.hand')}</span>
          <div className="payer">
            {opt('right', hand, setHand, t('co.pp.handRight'), '')}
            {opt('left', hand, setHand, t('co.pp.handLeft'), '')}
          </div>
        </div>
        <label className="mf">
          <span className="mf__label">{t('co.pp.playtomicLabel')}</span>
          <input id="pp-playtomic" inputMode="decimal" placeholder={t('co.pp.playtomicPlaceholder')} value={pt}
            onChange={(e) => setPt(e.target.value.replace(',', '.'))} />
        </label>
        <div className="sheet__actions">
          <button className="paybar__opt" onClick={onClose}>{t('co.pp.cancel')}</button>
          <button className="savebtn" disabled={busy} onClick={save}>{busy ? t('co.pp.saving') : t('co.set.save')}</button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ---------------------------------------------------------------------
//  Level
// ---------------------------------------------------------------------

/** The level three months ago, or the first one set, to measure change against. */
function levelThen(history: NonNullable<Profile['level']>['history']) {
  const cutoff = Date.now() - 90 * 86400000;
  const before = history.filter((h) => new Date(h.at).getTime() <= cutoff);
  return before.length ? before[before.length - 1] : history[0];
}

export function LevelCard({ level, playtomic, name, onSet }: {
  level: Profile['level']; playtomic: number | null; name: string; onSet: () => void;
}) {
  const t = useT();
  if (!level) {
    return (
      <button className="pp-level pp-level--empty" onClick={() => { haptic.tap(); onSet(); }}>
        <span className="pp-level__label">{t('co.pp.level')}</span>
        <span className="pp-level__cta">{t('co.pp.setLevel')}</span>
        <span className="pp-level__hint">
          {t('co.pp.levelHint')}{playtomic != null ? t('co.pp.playtomicSays', { n: playtomic }) : ''}
        </span>
      </button>
    );
  }
  const then = levelThen(level.history);
  const delta = Math.round((level.current - then.level) * 10) / 10;
  const pct = (level.current / 7) * 100;
  return (
    <button className="pp-level" onClick={() => { haptic.tap(); onSet(); }}>
      <div className="pp-level__top">
        <div>
          <span className="pp-level__label">{t('co.pp.levelSetByYou')}</span>
          <div className="pp-level__row">
            <span className="pp-level__num">{fmtNum(level.current, 1)}</span>
            {delta !== 0 && level.history.length > 1 && (
              <span className={delta > 0 ? 'pp-up' : 'pp-down'}>
                {delta > 0 ? '▲' : '▼'} {t('co.pp.since', { delta: Math.abs(delta).toFixed(1), month: monthName(then.at) })}
              </span>
            )}
          </div>
        </div>
        <span className="pp-pill pp-pill--blue">{levelWord(level.current)}</span>
      </div>
      <div className="pp-bar" aria-hidden="true">
        <span className="pp-bar__fill" style={{ width: `${pct}%` }} />
        <span className="pp-bar__dot" style={{ left: `calc(${pct}% - 8px)` }} />
      </div>
      <div className="pp-bar__scale" aria-hidden="true"><span>0</span><span>{t('co.pp.scaleMid')}</span><span>7</span></div>
      <div className="pp-chips">
        <span className="pp-pill pp-pill--teal">{t('co.pp.yourAssessment', { date: shortDate(level.setAt) })}</span>
        {playtomic != null && <span className="pp-pill">{t('co.pp.playtomicRef', { n: playtomic })}</span>}
      </div>
    </button>
  );
}

export function LevelSheet({ clientId, name, current, onClose, onSaved }: {
  clientId: string; name: string; current: number | null; onClose: () => void; onSaved: () => void;
}) {
  const t = useT();
  const [value, setValue] = useState(current ?? 3);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  async function save() {
    setBusy(true);
    try {
      await api(`/api/coach/clients/${clientId}/level`, {
        method: 'PUT', body: JSON.stringify({ level: value, note }),
      });
      haptic.success(); onSaved(); onClose();
    } catch (err: any) {
      haptic.error(); await alert(t('co.pp.couldNotSave', { message: err.message }));
    } finally { setBusy(false); }
  }

  return createPortal(
    <div className="sheet" onClick={onClose}>
      <div className="sheet__panel sheet--rhythm" onClick={(e) => e.stopPropagation()}>
        <div className="sheet__title">{t('co.pp.levelTitle', { name })}</div>
        <div className="pp-levelpick">
          <button type="button" className="pp-step" aria-label={t('co.pp.lower')}
            onClick={() => { haptic.tap(); setValue((v) => Math.max(0, Math.round((v - 0.1) * 10) / 10)); }}>−</button>
          <div>
            <div className="pp-level__num">{fmtNum(value, 1)}</div>
            <div className="card__when" style={{ textAlign: 'center' }}>{levelWord(value)}</div>
          </div>
          <button type="button" className="pp-step" aria-label={t('co.pp.raise')}
            onClick={() => { haptic.tap(); setValue((v) => Math.min(7, Math.round((v + 0.1) * 10) / 10)); }}>+</button>
        </div>
        <input id="pp-level-range" type="range" min={0} max={7} step={0.1} value={value}
          aria-label={t('co.pp.level')} style={{ width: '100%' }}
          onChange={(e) => setValue(Number(e.target.value))} />
        <label className="mf">
          <span className="mf__label">{t('co.pp.levelWhy')}</span>
          <input id="pp-level-note" placeholder={t('co.pp.levelWhyPlaceholder')} value={note}
            onChange={(e) => setNote(e.target.value)} />
        </label>
        <div className="card__when">{t('co.pp.levelKept')}</div>
        <div className="sheet__actions">
          <button className="paybar__opt" onClick={onClose}>{t('co.pp.cancel')}</button>
          <button className="savebtn" disabled={busy || value === current} onClick={save}>
            {busy ? t('co.pp.saving') : t('co.pp.saveLevel')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** Level over time as a small area line. Needs at least two points to be a line. */
export function LevelTrend({ history }: { history: NonNullable<Profile['level']>['history'] }) {
  const t = useT();
  if (history.length < 2) return null;
  const w = 330, h = 80;
  const vals = history.map((p) => p.level);
  const lo = Math.max(0, Math.min(...vals) - 0.3), hi = Math.min(7, Math.max(...vals) + 0.3);
  const t0 = new Date(history[0].at).getTime(), t1 = new Date(history[history.length - 1].at).getTime();
  const pts = history.map((p) => [
    5 + ((new Date(p.at).getTime() - t0) / Math.max(1, t1 - t0)) * (w - 10),
    h - 6 - ((p.level - lo) / Math.max(0.1, hi - lo)) * (h - 12),
  ]);
  const line = pts.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const last = pts[pts.length - 1];
  return (
    <div className="card pp-card">
      <div className="pp-card__head"><span className="sec2">{t('co.pp.levelOverTime')}</span>
        <span className="card__when">{shortDate(history[0].at)} → {shortDate(history[history.length - 1].at)}</span></div>
      <svg viewBox={`0 0 ${w} ${h}`} width="100%" height={h} role="img"
        aria-label={t('co.pp.levelFromTo', { from: vals[0], to: vals[vals.length - 1] })}>
        <polygon points={`5,${h} ${line} ${w - 5},${h}`} fill="var(--accent)" opacity=".14" />
        <polyline points={line} fill="none" stroke="var(--accent)" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
        <circle cx={last[0]} cy={last[1]} r="4.5" fill="var(--bg)" stroke="var(--accent)" strokeWidth="2.5" />
      </svg>
      <div className="pp-card__foot"><span>{fmtNum(vals[0], 1)}</span><span>{fmtNum(vals[vals.length - 1], 1)}</span></div>
    </div>
  );
}

// ---------------------------------------------------------------------
//  Sessions
// ---------------------------------------------------------------------

export function SessionRowCard({ s, onOpen, onRate, onPay }: {
  s: SessionRow; onOpen: () => void; onRate?: () => void; onPay?: () => void;
}) {
  const t = useT();
  const missed = s.status === 'no_show';
  const rating = missed ? <span className="srow__missed">{t('co.tbl.missed')}</span>
    : s.rated ? <span className="srow__rated"><CheckIcon />{t('co.tbl.rated')}</span>
    : onRate ? <button className="srow__rate" onClick={onRate}>{t('co.tbl.rate')}</button>
    : t('co.profile.notRated');
  // Still owed: «80 € ›» opens «Оплата», as the lesson panel does.
  const owed = Number(s.owed ?? 0);
  return (
    <SessionLine
      starts={s.starts_at}
      title={s.title}
      kind={s.kind}
      people={s.people}
      missed={missed}
      end={onPay && owed > 0
        ? <span className="srow__ends"><button className="srow__owed" aria-label={t('lp.due', { amount: fmtMoney(owed) })}
            onClick={onPay}>{fmtMoney(owed)} ›</button>{rating}</span>
        : rating}
      onOpen={onOpen} />
  );
}

export function SessionsTab({ clientId, name, onOpen, onRate, onPaid }: {
  clientId: string; name?: string; onOpen: (bookingId: string) => void; onRate?: (s: SessionRow) => void;
  /** After «Оплата» from a row: the card's balance and money change too. */
  onPaid?: () => void;
}) {
  const t = useT();
  const [data, setData] = useState<{ stats: any; focus: { tag: string; count: number }[]; sessions: SessionRow[] } | null>(null);
  const [paying, setPaying] = useState<SessionRow | null>(null);

  const load = () => api<any>(`/api/coach/clients/${clientId}/sessions`).then(setData)
    .catch(() => setData({ stats: { sessions: 0, hours: 0, turnedUp: null }, focus: [], sessions: [] }));
  useEffect(() => { load(); }, [clientId]);

  const shown = data?.sessions ?? [];

  if (!data) return <div className="list"><div className="skeleton" style={{ height: 120 }} /><div className="skeleton" /></div>;

  const focusTotal = data.focus.reduce((a, f) => a + f.count, 0);
  const COLORS = ['var(--accent)', 'var(--court)', '#8b5cf6', 'var(--held)', '#e05c5c', '#64748b'];
  // Counted from the rows themselves, so the line always agrees with the table above it.
  const missed = shown.filter((s) => s.status === 'no_show').length;

  if (shown.length === 0) return <div className="list"><div className="empty" style={{ padding: 24 }}>{t('co.pp.nothingYet')}</div></div>;

  return (
    <div className="list">
      {byMonth(shown, (s) => s.starts_at).map(([month, rows], i) => (
        <div key={month}>
          <div className="srows__month">{month}</div>
          {i === 0 && <SessionHead last={t('co.tbl.rating')} />}
          <div className="srows srows--card">
            {rows.map((s) => (
              <SessionRowCard key={s.booking_id} s={s} onOpen={() => onOpen(s.booking_id)}
                onRate={onRate ? () => onRate(s) : undefined}
                onPay={name ? () => { haptic.tap(); setPaying(s); } : undefined} />
            ))}
          </div>
        </div>
      ))}

      <p className="psum__attendance">
        {t('co.tbl.came', { came: shown.length - missed, total: shown.length })}
        {missed > 0 && <> · {t('co.tbl.missedN', { n: missed })}</>}
      </p>

      {paying && name && (
        <LessonPaySheet bookingId={paying.booking_id} name={name}
          onClose={() => setPaying(null)} onDone={() => { load(); onPaid?.(); }} />
      )}

      {/* Only once there is something to show: an empty bar teaches less than nothing. */}
      {focusTotal > 0 && (
        <div className="psum">
          <div className="card__when">{t('co.pp.whereTime')}</div>
          <div className="pp-split">
            {data.focus.slice(0, 6).map((f, i) => (
              <span key={f.tag} style={{ width: `${(f.count / focusTotal) * 100}%`, background: COLORS[i] }} />
            ))}
          </div>
          <div className="pp-legend">
            {data.focus.slice(0, 6).map((f, i) => (
              <span key={f.tag}><i style={{ background: COLORS[i] }} />{f.tag} {Math.round((f.count / focusTotal) * 100)}%</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------
//  One session
// ---------------------------------------------------------------------

export function SessionView({ bookingId, onBack }: { bookingId: string; onBack: () => void }) {
  const t = useT();
  const [s, setS] = useState<any | null>(null);
  const [axes, setAxes] = useState<string[]>([]);
  const [editing, setEditing] = useState(false);
  const load = () => api<any>(`/api/coach/bookings/${bookingId}/detail`).then(setS).catch(() => setS(false));
  useEffect(() => {
    load();
    api<any[]>('/api/rating-axes').then((a) => setAxes(a.map((x) => x.name))).catch(() => {});
    window.scrollTo(0, 0);
  }, [bookingId]);

  if (s === null) return <div className="list"><div className="skeleton" style={{ height: 140 }} /></div>;
  if (s === false) return <div className="empty">{t('lv.couldNotOpen')}</div>;

  const drills: string[] = (s.drills ?? '').split('\n').map((d: string) => d.trim()).filter(Boolean);
  const when = `${fmtLongDay(new Date(s.starts_at))} · ${fmtTime(s.starts_at)}–${fmtTime(s.ends_at)}${s.location ? ` · ${s.location}` : ''}`;

  return (
    <div className="list pp-session" data-layer>
      <button className="detailhead__back" data-back onClick={onBack}>{t('lv.back')}</button>

      <div className="card pp-hero">
        <div className="card__when">{when}</div>
        <div className="pp-hero__title">{s.title}</div>
        <div className="pp-tags">
          {s.status === 'no_show' && <span className="pp-tag pp-tag--bad">{t('co.today.noShow')}</span>}
          {(s.focus ?? []).map((f: string) => <span key={f} className="pp-tag">{f}</span>)}
          <span className="pp-tag pp-tag--plain">
            {s.payment_mode === 'cash'
              ? fmtMoney(s.amount_due) + (Number(s.credits_charged) > 0 ? ` + ${String(Math.round(Number(s.credits_charged) * 100) / 100).replace('.', getLang() === 'ru' ? ',' : '.')} ${t('co.pp.crShort')}` : '')
              : t('lv.nCredits', { n: Number(s.credits_charged) || 1, credits: t.word('lv.credits', Number(s.credits_charged) || 1) })}
          </span>
        </div>
      </div>

      <LessonPayBlock bookingId={bookingId} name={s.first_name} owed={Number(s.owed ?? 0)} onDone={load} />

      {editing ? (
        <SessionEditor s={s} axes={axes} onCancel={() => setEditing(false)}
          onSaved={() => { setEditing(false); load(); }} />
      ) : (
        <>
          <div className="card pp-card">
            <div className="pp-card__head"><span className="sec2">{t('jr.drills')}</span>
              <Vis who="player" />
              <button className="sess__link" onClick={() => setEditing(true)}>{t('co.pp.edit')}</button></div>
            {drills.length ? drills.map((d, i) => (
              <div key={i} className="pp-drill"><span>{i + 1}</span>{d}</div>
            )) : <div className="card__when">{t('lv.noDrills')}</div>}
          </div>

          <div className="card pp-card">
            <div className="pp-card__head"><span className="sec2">{t('lv.ratings')}</span>
              <span className="card__when">{t('lv.vsLast')}</span></div>
            {s.ratings.length ? s.ratings.map((r: any) => {
              const d = r.previous == null ? null : r.score - r.previous;
              return (
                <div key={r.axis_id} className="pp-rate">
                  <span className="pp-rate__name">{r.name}</span>
                  <span className={`pp-rate__d${d != null && d > 0 ? ' pp-good' : d != null && d < 0 ? ' pp-warn' : ''}`}>
                    {d == null ? t('lv.first') : d === 0 ? '=' : `${d > 0 ? '+' : ''}${d}`}
                  </span>
                  <span className="pp-rate__boxes" aria-hidden="true">
                    {Array.from({ length: 10 }, (_, i) => <i key={i} className={i < r.score ? 'on' : ''} />)}
                  </span>
                  <b>{r.score}</b>
                </div>
              );
            }) : <div className="card__when">{t('lv.notRated')}</div>}
          </div>

          {(PLAYER_SEES_SHARED_NOTES || s.shared_note || s.homework) && (
            <div className="card pp-card pp-card--shared">
              <div className="pp-card__head"><span className="sec2">{t('jr.shared', { name: s.first_name })}</span>
                <Vis who="player" /></div>
              <div className="pp-text">{s.shared_note ?? <span className="card__when">{t('lv.noNote')}</span>}</div>
              {s.homework && <div className="pp-homework"><span>🎯</span><span><b>{t('lv.homework')}</b> {s.homework}</span></div>}
            </div>
          )}

          <div className="card pp-card">
            <div className="pp-card__head"><span className="sec2">{t('jr.private')}</span><Vis who="me" /></div>
            <div className="pp-text">{s.coach_note ?? <span className="card__when">{t('lv.noPrivate')}</span>}</div>
          </div>

          {s.client_note && (
            <div className="card pp-card">
              <span className="sec2">{t('jr.asked', { name: s.first_name })}</span>
              <div className="pp-text">{s.client_note}</div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function SessionEditor({ s, axes, onCancel, onSaved }: { s: any; axes: string[]; onCancel: () => void; onSaved: () => void }) {
  const t = useT();
  const [focus, setFocus] = useState<string[]>(s.focus ?? []);
  const [custom, setCustom] = useState('');
  const [drills, setDrills] = useState(s.drills ?? '');
  const [shared, setShared] = useState(s.shared_note ?? '');
  const [homework, setHomework] = useState(s.homework ?? '');
  const [priv, setPriv] = useState(s.coach_note ?? '');
  const [busy, setBusy] = useState(false);
  const options = [...new Set([...axes, ...focus])];

  async function save() {
    setBusy(true);
    try {
      await api(`/api/coach/bookings/${s.booking_id}/details`, {
        method: 'PUT', body: JSON.stringify({
          focus, drills,
          // Omitted while the fields are hidden: the route leaves a field
          // it is not sent alone, so anything already written survives.
          ...(PLAYER_SEES_SHARED_NOTES ? { shared_note: shared, homework } : {}),
        }),
      });
      if ((priv || null) !== (s.coach_note || null)) {
        await api(`/api/coach/bookings/${s.booking_id}/note`, { method: 'PUT', body: JSON.stringify({ note: priv }) });
      }
      haptic.success(); onSaved();
    } catch (err: any) {
      haptic.error(); await alert(t('co.pp.couldNotSave', { message: err.message }));
    } finally { setBusy(false); }
  }

  return (
    <div className="card pp-card pp-editor">
      <FocusPicker options={options} value={focus} onChange={setFocus} custom={custom} setCustom={setCustom} />
      <label className="mf"><span className="mf__label"><VisLabel text={t('co.rate.drillsLabel')} who="player" /></span>
        <GrowArea id="pp-drills" className="notearea" rows={3} value={drills} onChange={(e) => setDrills(e.target.value)}
          placeholder={t('co.pp.drillsPlaceholder')} /></label>
      <DrillSuggest value={drills} onChange={setDrills} />
      {PLAYER_SEES_SHARED_NOTES && (
        <>
          <label className="mf"><span className="mf__label"><VisLabel text={t('co.rate.sharedLabel', { name: s.first_name })} who="player" /></span>
            <GrowArea id="pp-shared" className="notearea" rows={2} value={shared} onChange={(e) => setShared(e.target.value)}
              placeholder={t('co.pp.sharedPlaceholder')} /></label>
          <label className="mf"><span className="mf__label"><VisLabel text={t('jr.homework')} who="player" /></span>
            <GrowArea id="pp-homework" className="notearea" rows={1} value={homework} onChange={(e) => setHomework(e.target.value)}
              placeholder={t('co.pp.homeworkPlaceholder')} /></label>
        </>
      )}
      <label className="mf"><span className="mf__label"><VisLabel text={t('jr.private')} who="me" /></span>
        <GrowArea id="pp-private" className="notearea" rows={2} value={priv} onChange={(e) => setPriv(e.target.value)} /></label>
      <div className="sheet__actions">
        <button className="paybar__opt" onClick={onCancel}>{t('co.pp.cancel')}</button>
        <button className="savebtn" disabled={busy} onClick={save}>{busy ? t('co.pp.saving') : t('co.rate.save')}</button>
      </div>
    </div>
  );
}

/** Pick what a session was about: the coach's rating skills, plus anything typed. */
export function FocusPicker({ options, value, onChange, custom, setCustom }: {
  options: string[]; value: string[]; onChange: (v: string[]) => void; custom: string; setCustom: (v: string) => void;
}) {
  const t = useT();
  const toggle = (tag: string) => onChange(value.includes(tag) ? value.filter((x) => x !== tag) : [...value, tag].slice(0, 6));
  return (
    <div>
      <span className="mf__label"><VisLabel text={t('co.rate.aboutLabel')} who="player" /></span>
      <div className="pp-tagpick">
        {options.map((tag) => (
          <button key={tag} type="button" className="chip" aria-pressed={value.includes(tag)}
            onClick={() => { haptic.tap(); toggle(tag); }}>{tag}</button>
        ))}
        <input id="pp-focus-custom" className="pp-tagpick__add" placeholder={t('co.rate.other')} value={custom}
          onChange={(e) => setCustom(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && custom.trim()) { e.preventDefault(); toggle(custom.trim()); setCustom(''); }
          }}
          onBlur={() => { if (custom.trim()) { toggle(custom.trim()); setCustom(''); } }} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
//  AI notes
// ---------------------------------------------------------------------

export function AiNotes({ clientId, name, onUseFocus }: {
  /** The suggested goal: what the player will read, and the coach's version of it. */
  clientId: string; name: string; onUseFocus: (player: string, coach: string) => void;
}) {
  const t = useT();
  const [data, setData] = useState<any | null>(null);
  const [busy, setBusy] = useState(false);
  const [showSources, setShowSources] = useState(false);
  const [helpTheme, setHelpTheme] = useState<string | null>(null);

  const load = () => api<any>(`/api/coach/clients/${clientId}/ai-summary`).then(setData)
    .catch(() => setData({ configured: false, hasInput: false, current: null }));
  useEffect(() => { load(); }, [clientId]);

  async function run() {
    setBusy(true);
    try {
      const res = await api<any>(`/api/coach/clients/${clientId}/ai-summary`, { method: 'POST', body: '{}' });
      haptic.success();
      setData({ ...data, current: res });
    } catch (err: any) {
      haptic.error();
      await alert(
        err.message === 'ai_not_configured' ? t('co.pp.aiNotSetUp')
          : err.message === 'nothing_to_read' ? t('co.pp.aiNothing')
          : err.message === 'ai_refused' ? t('co.pp.aiRefused')
          : t('co.pp.aiFailed'));
    } finally { setBusy(false); }
  }

  if (!data) return <div className="skeleton" style={{ height: 120 }} />;

  // Added by hand, without Telegram: never sent to the AI (sql/030).
  if (data.notInBot) {
    return (
      <div className="card pp-ai pp-ai--off">
        <div className="pp-ai__label">{t('co.pp.aiTitle')}</div>
        <div className="pp-text">{t('co.off.aiOff')}</div>
      </div>
    );
  }

  if (!data.configured) {
    return (
      <div className="card pp-ai pp-ai--off">
        <div className="pp-ai__label">{t('co.pp.aiTitle')}</div>
        <div className="pp-text">{t('co.pp.aiOffCard')}</div>
      </div>
    );
  }

  const cur = data.current;
  if (!cur) {
    return (
      <div className="card pp-ai">
        <div className="pp-ai__label">{t('co.pp.aiTitle')}</div>
        <div className="pp-text">
          {data.hasInput
            ? t('co.pp.aiBlurb')
            : t('co.pp.aiNotYet')}
        </div>
        <button className="savebtn" disabled={busy || !data.hasInput} onClick={run}>
          {busy ? t('co.pp.aiReading') : t('co.pp.aiRead')}
        </button>
        <div className="card__when">{t('co.pp.aiPrivate')}</div>
      </div>
    );
  }

  /** What it was read from, kind by kind, leaving out what there was none of. */
  const basedOn = (c: { notes: number; goals?: number; plans?: number; playerNotes: number; ratingAxes: number }) => {
    const parts = [
      c.notes && `${fmtNum(c.notes)} ${t.word('co.pp.notes', c.notes)}`,
      c.goals && `${fmtNum(c.goals)} ${t.word('co.pp.goals', c.goals)}`,
      c.plans && `${fmtNum(c.plans)} ${t.word('co.pp.plans', c.plans)}`,
      c.playerNotes && `${fmtNum(c.playerNotes)} ${t.word('co.pp.requests', c.playerNotes)}`,
      c.ratingAxes && t('co.pp.ratingsOn', { n: c.ratingAxes, skills: t.word('co.pp.skillsDat', c.ratingAxes) }),
    ].filter(Boolean);
    return t('co.pp.basedOn', { list: parts.join(', ') });
  };

  const s = cur.summary;
  // A recurring theme opens «Что помогало с этим у других» (not a health one).
  const theme = (t: any) => (
    <button key={t.label} className={`pp-pill pp-pill--tap ${t.kind === 'strength' ? 'pp-pill--teal' : t.kind === 'work' ? 'pp-pill--amber' : ''}`}
      disabled={t.kind === 'health'} onClick={() => { haptic.tap(); setHelpTheme(t.label); }}>
      <span className="pp-pill__text">{t.kind === 'health' ? '🔒 ' : ''}{t.label}</span><b> ×{t.count}</b>
    </button>
  );

  return (
    <>
      <div className="card pp-ai">
        <div className="pp-card__head">
          <span className="pp-ai__label">{t('co.pp.aiTitle')}</span>
          {cur.stale && (
            <span className="pp-pill pp-pill--amber">
              {cur.staleReason === 'notes' ? t('co.pp.aiStale') : t('co.pp.aiStaleLang')}
            </span>
          )}
        </div>
        {/* The five-block report; older bullets or a paragraph show until refreshed. */}
        {s.blocks ? (
          <div className="pp-ai__blocks">
            {(['overview', 'better', 'stuck', 'next', 'homework'] as const).filter((k) => s.blocks[k]).map((k) => (
              <div key={k} className="pp-ai__block">
                {/* «Пока мало данных» is the overview alone: no heading over it. */}
                {(k !== 'overview' || s.blocks.better) && <span className="sec2">{t(`co.pp.blk.${k}` as 'co.pp.blk.overview')}</span>}
                <p>{s.blocks[k]}</p>
              </div>
            ))}
          </div>
        ) : Array.isArray(s.bullets) ? (
          <ul className="pp-ai__points">{s.bullets.map((b: string, i: number) => <li key={i}>{b}</li>)}</ul>
        ) : (
          <div className="pp-ai__text">{s.summary}</div>
        )}
        <div className="card__when">
          {basedOn(cur.counts)} · {shortDate(cur.createdAt)}
        </div>
        <div className="pp-row2">
          <button className="paybar__opt" onClick={() => setShowSources(!showSources)}>
            {showSources ? t('co.pp.hideSources') : t('co.pp.showSources')}
          </button>
          <button className="paybar__opt" disabled={busy} onClick={run}>{busy ? t('co.pp.aiReading') : t('co.pp.aiRefresh')}</button>
        </div>
        {showSources && (
          <div className="pp-sources">
            {cur.sources.map((src: any, i: number) => (
              <div key={i}><span className="card__when">{src.date ? shortDate(src.date) : t('co.pp.src.undated')} · {sourceKind(src.kind)}</span>{src.text}</div>
            ))}
          </div>
        )}
      </div>

      {s.themes.length > 0 && (
        <div className="card pp-card">
          <span className="sec2">{t('co.pp.aiThemes')}</span>
          <div className="pp-chips">{s.themes.map(theme)}</div>
          {s.themes.some((t: any) => t.kind === 'health') && (
            <div className="card__when">{t('co.pp.aiHealthPrivate', { name })}</div>
          )}
        </div>
      )}

      {helpTheme && <ThemeHelpSheet clientId={clientId} theme={helpTheme} onClose={() => setHelpTheme(null)} />}

      {(s.goal_coach || s.suggested_focus) && (
        <div className="card pp-card pp-card--shared">
          <span className="sec2">{t('co.pp.aiFocus')}</span>
          {/* The coach reads it in their language; the player gets it in theirs. */}
          <div className="pp-text">{s.goal_coach || s.suggested_focus}</div>
          {s.goal_player && s.goal_coach && s.goal_player !== s.goal_coach && (
            <div className="card__when">{t('co.pp.aiFocusPlayer', { text: s.goal_player })}</div>
          )}
          <button className="savebtn" onClick={() => onUseFocus(s.goal_player || s.suggested_focus, s.goal_coach || s.goal_player || s.suggested_focus)}>{t('co.pp.aiUseFocus')}</button>
        </div>
      )}
    </>
  );
}
